import type { ConfigOptionals } from '@/modules/config.interface';
import type { AppConfig } from './AppConfig';
import { useValidationSchema as useValidationSchemaTelefonica } from '@/custom/telefonica/useValidationSchema';
import { getInitialFormState as getInitialFormStateTelefonica } from '@/custom/telefonica/getInitialFormState';
import { languages } from '@/shared';

const corporatesConfig: Record<
	string,
	Partial<{
		appConfig: Partial<AppConfig>;
		homeConfig: ConfigOptionals;
		campaignInfoConfig: Omit<ConfigOptionals, 'merchantLogo'>;
		donateConfig: Omit<ConfigOptionals, 'merchantLogo'>;
	}>
> = {
	actitudsolidaria: {
		homeConfig: {
			disableLogoInHomePage: true,
			disableCounters: true,
			footerTextColor: 'white',
			descriptionTextColor: 'white',
			actitudSolidariaPresentation: true,
			fundedByEmployeesCampaigns: ['406098ca-35e6-472d-a103-7b2d57e9b652', 'f50ea45a-230b-44ad-8fdf-95291bf7b5b4'],
		},
		campaignInfoConfig: {
			disableMerchantLogo: true,
			actitudSolidariaPresentation: true,
		},
		donateConfig: {
			disableMerchantLogo: true,
			actitudSolidariaPresentation: true,
		},
	},
	alainafflelou: {
		appConfig: {
			customDonors: (donors, campaignId) =>
				// Correct donors deviation for this campaign, on Home campaignId will be undefined
				campaignId === '51d698ef-596c-4d34-9e87-81d4c37516d7' || campaignId == null ? donors - 7 : donors,
		},
	},
	alala: {
		campaignInfoConfig: {
			campaignDonateButtonUp: true,
			disableTexts: {
				updatesAndLinks: true,
				activities: true,
				objectives: true,
				whoWillBenefit: true,
			},
			showCampaignResumeShortKnowMoreButton: true,
			formatBeneficiariesAsSpanishNumber: true,
			logoHeight: 150,
			showAlalaWebm: true,
			knowMoreButton: true,
			isAlala: true,
		},
		donateConfig: {
			doYourDonationTextCenter: true,
			isAlala: true,
			logoHeight: 150,
		},
	},
	alfil: {
		homeConfig: {
			leftColumnBackgroundColor: '#f9f9f9',
		},
	},
	ametllerorigen: {
		homeConfig: {
			blacklistCampaigns: ['82f9967a-f59b-4b48-9916-62750a341d54'],
		},
	},
	andorrasolidaria: {
		appConfig: {
			getMerchantLogoUrl: (campaignId: string) =>
				campaignId === '79ccd0f9-1030-4877-acb4-4f8b611098de'
					? '/muntanyencs_himalaia.png'
					: campaignId === 'f9fe8ce9-b88c-4d40-8e59-8fdb44eaf115'
						? '/andorra_telecom.png'
						: undefined,
		},
		homeConfig: {
			merchantLogo: () => 'https://cdn.worldcoo.com/corporates/assets/andorra_solidaria.png',
			leftColumnBackgroundColor: '#f9f9f9',
			bottomLogo: 'https://cdn.worldcoo.com/corporates/assets/univers_bomosapng.png',
		},
		campaignInfoConfig: {
			logoHeight: 150,
		},
	},
	areas: {
		homeConfig: {
			leftColumnBackgroundColor: '#f9f9f9',
		},
		campaignInfoConfig: {
			disableCampaignBeneficiaries: (campaignId) => campaignId === '1c0269da-2061-48fe-8f54-52d92f802306',
			customCountries: () => '',
		},
		donateConfig: {
			hideCertificateOption: true,
		},
	},
	awwg: {
		homeConfig: {
			leftColumnBackgroundColor: '#f9f9f9',
			logoHeight: '300px',
			logoWidth: () => '300px',
		},
		campaignInfoConfig: {
			disableMerchantLogo: true,
		},
		donateConfig: {
			disableMerchantLogo: true,
		},
	},
	bonarea: {
		homeConfig: {
			leftColumnBackgroundColor: '#f9f9f9',
			merchantLogo: () => 'https://cdn.worldcoo.com/corporates/assets/bonarea-logo-png-cat.png',
			disableTitle: true,
			logoWidth: () => '300px',
			logoHeight: '60px',
			disableTagLine: true,
		},
	},
	bonpreu: {
		homeConfig: {
			disableLogoInHomePage: true,
			descriptionTextColor: 'white',
			footerTextColor: 'white',
			hideCollectedCounter: true,
		},
		campaignInfoConfig: {
			hideCollectedCounter: true,
		},
		donateConfig: {
			hideCollectedCounter: true,
		},
	},
	caixabank: {
		homeConfig: {
			leftColumnBackgroundColor: '#f9f9f9',
			logoWidth: () => '400px',
			logoHeight: '40px',
		},
	},
	caprabo: {
		homeConfig: {
			merchantLogo: () => 'https://cdn.worldcoo.com/corporates/assets/caprabo_saludables_sostenibles.png',
			preCountersLogo: () => 'https://cdn.worldcoo.com/corporates/assets/caprabo_white.png',
		},
	},
	clubvalvi: {
		homeConfig: {
			leftColumnBackgroundColor: '#f9f9f9',
			disableTitle: true,
			logoWidth: () => '300px',
			logoHeight: '90px',
		},
	},
	comerciaglobalpayments: {
		homeConfig: {
			leftColumnBackgroundColor: '#f9f9f9',
			mapCampaigns: (campaigns) => [
				...campaigns.filter((campaign) => campaign.id === '6cef5aae-17f5-426e-8997-17500aebf873'), // ukraine campaign
				...campaigns.filter((campaign) => campaign.id !== '6cef5aae-17f5-426e-8997-17500aebf873'), // ukraine campaign
			],
		},
	},
	condis: {
		homeConfig: {
			merchantLogo: () => 'https://cdn.worldcoo.com/corporates/assets/condis-spa.png',
			logoWidth: () => '320px',
			logoHeight: '136px',
			disableTitle: true,
		},
	},
	correos: {
		homeConfig: {
			leftColumnBackgroundColor: '#f9f9f9',
			disableTitle: true,
			logoHeight: '150px',
			logoWidth: () => '150px',
		},
	},
	damm: {
		homeConfig: {
			merchantLogo: () => 'https://cdn.worldcoo.com/corporates/assets/damm/damm_white.svg',
			descriptionTextColor: 'white',
			footerTextColor: 'white',
		},
	},
	delinas: {
		homeConfig: {
			logoHeight: '150px',
			logoWidth: () => '150px',
		},
	},
	doctipharma: {
		homeConfig: {
			logoHeight: '150px',
			logoWidth: () => '150px',
		},
	},
	drivim: {
		homeConfig: {
			disableLogoInHomePage: true,
			disableCounters: true,
		},
	},
	eroski: {
		homeConfig: {
			eroskiMerchantLogo: true,
			preCountersLogo: (currentLangCode) =>
				currentLangCode === languages.Gallego
					? 'https://cdn.worldcoo.com/corporates/assets/eroski_logo_es.png'
					: currentLangCode === languages.Euskera
						? 'https://cdn.worldcoo.com/corporates/assets/eroski_logo_eu.png'
						: currentLangCode === languages.Catalan
							? 'https://cdn.worldcoo.com/corporates/assets/eroski_logo_ca.png'
							: 'https://cdn.worldcoo.com/corporates/assets/eroski_logo_es.png',
			descriptionTextColor: 'white',
			footerTextColor: 'white',
		},
		campaignInfoConfig: {
			buttonColor: 'secondary',
		},
		donateConfig: {
			buttonColor: 'secondary',
		},
	},
	fundacionbarcelona: {
		homeConfig: {
			merchantLogo: () => 'https://cdn.worldcoo.com/corporates/assets/fcb/topbar_logos_es.png',
			logoWidth: () => '400px',
			logoHeight: '100px',
		},
	},
	fundacionrealmadrid: {
		homeConfig: {
			logoWidth: () => '400px',
			logoHeight: '400px',
		},
	},
	generaloptica: {
		homeConfig: {
			buttonColor: 'secondary',
			logoWidth: () => '200px',
			logoHeight: '100%',
			leftColumnBackgroundColor: '#fff',
			footerBackgroundColor: '#e3002b',
			footerTextColor: 'white',
			merchantLogo: (currentLangCode) =>
				currentLangCode === languages.Catalan
					? 'https://cdn.worldcoo.com/public/img/Logo_GeneralOptica_CAT.png'
					: 'https://cdn.worldcoo.com/public/img/Logo_GeneralOptica_Castellano.jpg.png',
		},
		campaignInfoConfig: {
			buttonColor: 'secondary',
			leftColumnBgColor: '#fff',
			logoHeight: 150,
			footerBackgroundColor: '#e3002b',
		},
		donateConfig: {
			buttonColor: 'secondary',
			footerBackgroundColor: '#e3002b',
			logoHeight: 150,
		},
	},
	glovo: {
		homeConfig: {
			merchantLogo: () => 'https://cdn.worldcoo.com/corporates/assets/glovo/logo_green.svg',
		},
	},
	h10hotels: {
		homeConfig: {
			leftColumnBackgroundColor: '#002D60',
			logoHeight: '90px',
		},
	},
	iberdrola: {
		homeConfig: {
			merchantLogo: () => 'https://cdn.worldcoo.com/corporates/assets/logo_iberdrola_coop.png',
			leftColumnBackgroundColor: '#f9f9f9',
			disableLogoInHomePage: true,
			disableCounters: true,
		},
		campaignInfoConfig: {
			disableMerchantLogo: true,
		},
		donateConfig: {
			disableMerchantLogo: true,
			certificateWarning: true,
		},
	},
	imagin: {
		homeConfig: {
			leftColumnBackgroundColor: '#f9f9f9',
			leftColumnVisible: (isDownXs) => !isDownXs,
			logoWidth: () => '137px',
			logoHeight: '100px',
		},
		campaignInfoConfig: {
			leftColumnVisible: (_isDownXs, isDownSm) => !isDownSm,
		},
		donateConfig: {
			leftColumnVisible: (isDownXs) => !isDownXs,
		},
	},
	movistarplus: {
		homeConfig: {
			merchantLogo: () => 'https://cdn.worldcoo.com/corporates/assets/movistar%2B_negativo.png',
			disableTitle: true,
			disableCounters: true,
			logoHeight: '70px',
		},
	},
	movistarteam: {
		homeConfig: {
			merchantLogo: () => 'https://cdn.worldcoo.com/corporates/assets/movistar/movistar_team.png',
			logoHeight: '160px',
			logoWidth: () => '400px',
		},
	},
	muymucho: {
		homeConfig: {
			leftColumnBackgroundColor: 'rgb(244, 229, 192)',
			disableTitle: true,
			merchantLogo: () => 'https://cdn.worldcoo.com/corporates/assets/muymucho-solidario-01.png',
			logoHeight: '81px',
			descriptionTextColor: 'black',
		},
	},
	nomo: {
		campaignInfoConfig: {
			leftColumnDescriptionTitlesColor: '#502C1E',
		},
	},
	palladiumcares: {
		homeConfig: {
			logoWidth: () => 300,
			logoHeight: 300,
		},
	},
	panet: {
		homeConfig: {
			leftColumnBackgroundColor: '#f9f9f9',
			logoHeight: '100px',
		},
	},
	patagonia: {
		homeConfig: {
			leftColumnBackgroundColor: '#0256b9',
			buttonColor: 'secondary',
			footerBackgroundColor: '#fbd304',
			buttonBgColor: '#fbd304',
			descriptionTextColor: '#fbd304',
			isInformativeCorporate: true,
		},
		campaignInfoConfig: {
			disableTexts: {
				activities: true,
				objectives: true,
				updatesAndLinks: true,
				whoWillBenefit: true,
			},
			showDonateButton: false,
			isInformativeCampaign: true,
		},
		donateConfig: {
			disableSeeCampaignInfo: true,
			buttonBgColor: '#fbd304',
			hideCertificateOption: true,
		},
	},
	pccomponentes: {
		homeConfig: {
			disableLogoInHomePage: true,
		},
	},
	portaventura: {
		homeConfig: {
			leftColumnBackgroundColor: '#f9f9f9',
			logoWidth: () => '140px',
			logoHeight: '100px',
		},
	},
	promofarma: {
		homeConfig: {
			disableLogoInHomePage: true,
			disableCounters: true,
		},
	},
	repsol: {
		homeConfig: {
			disableMerchantLogo: true,
			disableClaim: true,
		},
		campaignInfoConfig: {
			disableMerchantLogo: true,
			disableClaim: true,
		},
		donateConfig: {
			disableMerchantLogo: true,
		},
	},
	rodilla: {
		campaignInfoConfig: {
			leftColumnDescriptionTitlesColor: 'black',
		},
	},
	// rufa: {
	// 	appConfig: {
	// 		defaultCampaignId: '80466918-4280-4420-aa9e-30b86d4ff2a9',
	// 	},
	// 	campaignInfoConfig: {
	// 		disableTexts: {
	// 			resume: true,
	// 			updatesAndLinks: true,
	// 			objectives: true,
	// 			activities: true,
	// 			whoWillBenefit: true,
	// 		},
	// 		disableTitle: true,
	// 		disableCampaignBeneficiaries: () => true,
	// 		hideTotalDonationsAmountObjective: true,
	// 		hidePercentFunded: true,
	// 		customCountries: (campaignId) => (campaignId === '80466918-4280-4420-aa9e-30b86d4ff2a9' ? 'Europe' : undefined),
	// 	},
	// 	donateConfig: {
	// 		hideTotalDonationsAmountObjective: true,
	// 		hidePercentFunded: true,
	// 		customNgoHeaderCountries: (_currentLangCode, campaignId) =>
	// 			campaignId === '80466918-4280-4420-aa9e-30b86d4ff2a9' ? 'Europe' : undefined,
	// 		hideCertificateOption: true,
	// 		disableTitle: true,
	// 		disableSeeCampaignInfo: true,
	// 		showCampaignImageInDonatePage: true,
	// 	},
	// },
	sanchezromero: {
		homeConfig: {
			disableTitle: true,
			merchantLogo: () => 'https://cdn.worldcoo.com/corporates/assets/sanchez-romero-logo-white-small.png',
			logoWidth: () => '200px',
			logoHeight: '200px',
			disableCounters: true,
		},
	},
	soprasteria: {
		homeConfig: {
			disableLogoInHomePage: true,
			descriptionTextColor: 'white',
			footerTextColor: 'white',
		},
		campaignInfoConfig: {
			topBarLogoSrc: (merchantLogo) => merchantLogo,
			disableMerchantLogo: true,
		},
		donateConfig: {
			disableMerchantLogo: true,
			topBarLogoSrc: (merchantLogo) => merchantLogo,
			leftColumnBottomLogo: 'https://cdn.worldcoo.com/public/img/soprasteria-logos.png',
		},
	},
	ssp: {
		homeConfig: {
			descriptionTextColor: 'white',
			footerTextColor: 'white',
		},
	},
	telefonica: {
		homeConfig: {
			disableExtraTerms: true,
			disableLogoInHomePage: true,
			descriptionTextColor: 'white',
			footerTextColor: 'white',
		},
		campaignInfoConfig: { customCampaignByText: 'Una campaña a favor de' },
		donateConfig: {
			customExtraTermsContentTelefonicaAccessTicket:
				'<p><strong>AVISO LEGAL PARA LA TOMA DE DATOS DE CARÁCTER PERSONAL</strong></p><p>FUNDACIÓN TELEFÓNICA respeta la legislación vigente en materia de protección de datos personales, la privacidad de los usuarios y el secreto y seguridad de los datos personales, en concreto el Reglamento 2016/679 del Parlamento Europeo y del Consejo de 27 de abril de 2016, adoptando para ello las medidas técnicas y organizativas necesarias para evitar la pérdida, mal uso, alteración, acceso no autorizado y robo de los datos personales facilitados, habida cuenta del estado de la tecnología, la naturaleza de los datos y los riesgos a los que están expuestos.</p><p>Al efectuar tu donativo, los datos que facilites (nombre y apellidos, correo electrónico) serán tratados por Fundación Telefónica, con la finalidad de gestionar tu participación en esta iniciativa y, en su caso, enviarte comunicaciones de tu interés en relación al mismo y al Programa Voluntarios Telefónica. Tus datos podrán ser cedidos a terceros, para coordinar tu participación en la actividad. En el caso de ser empleado de una empresa del grupo Telefónica, Fundación Telefónica o Fundación ProFuturo, tus datos podrán ser cedidos a la correspondiente área de Personas de dichas entidades, para coordinar y gestionar tu participación en esta actividad de voluntariado corporativo.</p><p>&nbsp;</p><p>De acuerdo con la actual legislación, queda informado y acepta el tratamiento de sus datos con la finalidad descrita a continuación:</p><p>&nbsp;</p><p><strong>Información sobre protección de datos</strong></p><p>Responsable:&nbsp;Fundación Telefónica.</p><p>Finalidad del Tratamiento:&nbsp;Recogida de los datos de donantes que contribuyen a causas promovidas por Fundación Telefónica. Como entidad sin ánimo de lucro y dentro del desarrollo de sus fines fundacionales, Fundación Telefónica le mantendrá informado de su actividad.</p><p>Legitimación:&nbsp;Consentimiento del interesado y obligación legal</p><p>Cesiones:&nbsp;Administración tributaria.</p><p>Derechos:&nbsp;A retirar su consentimiento en cualquier momento, a oponerse al tratamiento, a acceder, rectificar y suprimir los datos, así como otros derechos, tal y como se explica en la información adicional.</p><p>Transferencias Internacionales:&nbsp;No se realizan transferencias internacionales de datos de carácter personal.</p><p>Información Adicional:&nbsp;<a href="https://legal.fundaciontelefonica.com/informacion-adicional-proteccion-de-datos/">https://legal.fundaciontelefonica.com/informacion-adicional-proteccion-de-datos/</a></p>',
			certificateWarning: true,
			customCampaignByText: 'Una campaña a favor de',
			disableCounters: true,
			isTelefonicaForm: true,
			useValidationSchema: useValidationSchemaTelefonica,
			getInitialFormState: getInitialFormStateTelefonica,
			metafields: (campaignId) =>
				campaignId === 'a6534c5a-b81b-4f84-a57c-30139aae252f'
					? {
							tickets: 0,
							noTicket: false,
						}
					: undefined,
		},
	},
	ticketmaster: {
		homeConfig: {
			descriptionTextColor: 'white',
			footerTextColor: 'white',
		},
	},
	verdecora: {
		homeConfig: {
			disableLogoInHomePage: true,
			preCountersLogo: (_currentLangCode, storeMerchantLogo) => storeMerchantLogo,
		},
	},
	viena: {
		homeConfig: {
			merchantLogo: (currentLangCode) => {
				const vienaLogosByLangMap: Record<string, string> = {
					[languages.Spanish]: 'https://cdn.worldcoo.com/corporates/assets/viena_es.png',
					[languages.Catalan]: 'https://cdn.worldcoo.com/corporates/assets/viena_cat.png',
				};

				return vienaLogosByLangMap[currentLangCode] ?? null;
			},
			disableTitle: true,
			logoWidth: (downSm, upMd) => (downSm ? '100%' : upMd ? '50%' : '70%'),
			logoHeight: 'auto',
			descriptionTextColor: 'white',
			footerTextColor: 'white',
		},
	},
	wala: {
		homeConfig: {
			leftColumnBackgroundColor: '#f9f9f9',
			merchantLogo: () => 'https://cdn.worldcoo.com/corporates/assets/wala_logo.png',
			logoWidth: () => '180px',
			logoHeight: '55px',
		},
	},
};

export function getCorporateconfig(corporateId: string): {
	appConfig: AppConfig;
	homeConfig: ConfigOptionals;
	campaignInfoConfig: Omit<ConfigOptionals, 'merchantLogo'>;
	donateConfig: Omit<ConfigOptionals, 'merchantLogo'>;
} {
	const config =
		corporateId in corporatesConfig ? corporatesConfig[corporateId as keyof typeof corporatesConfig] : undefined;

	return {
		homeConfig: {},
		campaignInfoConfig: {},
		donateConfig: {},
		...config,
		appConfig: {
			corporateId,
			...config?.appConfig,
		},
	};
}
